import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { isMobile } from 'react-device-detect';
import '../Components/CheckoutLeaveConfirm.scss';

export const CHECKOUT_PATHS = new Set(['/cart', '/order-summary']);

let previousPathname = '/';
let pathBeforeCheckout = '/';

/** Ignore leave-confirm briefly during intentional cart ↔ order-summary navigation. */
let checkoutStepTransitionUntil = 0;

export function allowCheckoutStepTransition(ms = 2500) {
  const until = Date.now() + ms;
  if (until > checkoutStepTransitionUntil) {
    checkoutStepTransitionUntil = until;
  }
}

function isCheckoutStepTransition() {
  return Date.now() < checkoutStepTransitionUntil;
}

/** Updated on every route change via CheckoutPathTracker. */
export function rememberPathForCheckoutLeave(pathname) {
  const enteringCheckout = CHECKOUT_PATHS.has(pathname);

  if (!enteringCheckout) {
    pathBeforeCheckout = pathname || '/';
  } else if (!CHECKOUT_PATHS.has(previousPathname)) {
    pathBeforeCheckout = previousPathname || '/';
  }

  previousPathname = pathname;
}

export function getCheckoutLeaveDestination(pathname) {
  if (pathname === '/order-summary') return '/cart';
  return pathBeforeCheckout || '/';
}

/** Shared state so HeaderMob can trigger the same confirm flow. */
export const mobCheckoutGuard = {
  active: false,
  isProcessing: false,
  promptLeave: null,
};

function shouldUseMobCheckoutGuard() {
  if (typeof window === 'undefined') return isMobile;
  return isMobile || window.matchMedia('(max-width: 768px)').matches;
}

/**
 * Normalize promptLeave args:
 * - no args / 'popstate' / 'header' → leave to back/source destination
 * - '/wishlist' → leave to that path
 * - { to, onAfterLeave } → leave to `to` (optional) then run callback
 */
function normalizeLeaveOptions(arg) {
  if (arg == null || arg === 'header' || arg === 'popstate' || arg === 'back') {
    return { to: null, onAfterLeave: null };
  }
  if (typeof arg === 'string') {
    return { to: arg, onAfterLeave: null };
  }
  if (typeof arg === 'object') {
    return {
      to: typeof arg.to === 'string' ? arg.to : null,
      onAfterLeave: typeof arg.onAfterLeave === 'function' ? arg.onAfterLeave : null,
    };
  }
  return { to: null, onAfterLeave: null };
}

function isOwnCheckoutTrap(state, ownedPath) {
  return Boolean(state?.mobCheckoutTrap && state.path === ownedPath);
}

export function useMobCheckoutLeaveConfirm(isProcessing = false, options = {}) {
  const { enabled = true } = options;
  const location = useLocation();
  const navigate = useNavigate();
  const [showConfirm, setShowConfirm] = useState(false);
  const showConfirmRef = useRef(false);
  const leaveConfirmedRef = useRef(false);
  const trapActiveRef = useRef(false);
  const trapPathRef = useRef(location.pathname);
  const pendingToRef = useRef(null);
  const pendingAfterLeaveRef = useRef(null);
  const isCheckoutPage = CHECKOUT_PATHS.has(location.pathname);
  const guardEnabled = Boolean(enabled) && shouldUseMobCheckoutGuard() && isCheckoutPage;

  showConfirmRef.current = showConfirm;

  const pushTrap = useCallback(() => {
    const path = location.pathname;
    window.history.pushState({ mobCheckoutTrap: true, path }, '');
    trapActiveRef.current = true;
    trapPathRef.current = path;
  }, [location.pathname]);

  const clearPendingLeave = useCallback(() => {
    pendingToRef.current = null;
    pendingAfterLeaveRef.current = null;
  }, []);

  const openLeaveConfirm = useCallback((arg) => {
    // Place Order / in-checkout step changes must not open this dialog.
    if (isCheckoutStepTransition()) {
      clearPendingLeave();
      return;
    }
    const { to, onAfterLeave } = normalizeLeaveOptions(arg);
    pendingToRef.current = to;
    pendingAfterLeaveRef.current = onAfterLeave;
    setShowConfirm(true);
  }, [clearPendingLeave]);

  const stayOnCheckout = useCallback(() => {
    clearPendingLeave();
    setShowConfirm(false);
  }, [clearPendingLeave]);

  const confirmLeave = useCallback(() => {
    let destination = pendingToRef.current || getCheckoutLeaveDestination(location.pathname);
    const onAfterLeave = pendingAfterLeaveRef.current;
    clearPendingLeave();

    if (!destination || destination === location.pathname) {
      destination = '/';
    }
    leaveConfirmedRef.current = true;
    setShowConfirm(false);
    mobCheckoutGuard.active = false;
    mobCheckoutGuard.isProcessing = false;
    mobCheckoutGuard.promptLeave = null;
    trapActiveRef.current = false;

    navigate(destination, { replace: true });
    onAfterLeave?.();

    window.setTimeout(() => {
      leaveConfirmedRef.current = false;
    }, 0);
  }, [clearPendingLeave, location.pathname, navigate]);

  useEffect(() => {
    if (!guardEnabled) {
      mobCheckoutGuard.active = false;
      mobCheckoutGuard.isProcessing = false;
      mobCheckoutGuard.promptLeave = null;
      return undefined;
    }

    mobCheckoutGuard.active = true;
    mobCheckoutGuard.isProcessing = isProcessing;
    mobCheckoutGuard.promptLeave = (arg) => openLeaveConfirm(arg);

    return () => {
      mobCheckoutGuard.active = false;
      mobCheckoutGuard.isProcessing = false;
      mobCheckoutGuard.promptLeave = null;
    };
  }, [guardEnabled, isProcessing, openLeaveConfirm]);

  useEffect(() => {
    if (!guardEnabled) return undefined;

    leaveConfirmedRef.current = false;
    trapActiveRef.current = false;
    setShowConfirm(false);
    pushTrap();

    const onPopState = () => {
      if (leaveConfirmedRef.current) return;

      trapActiveRef.current = false;

      if (isCheckoutStepTransition()) {
        // History noise from cart→summary transition / trap peel — stay put, re-arm.
        pushTrap();
        return;
      }

      if (showConfirmRef.current) {
        pushTrap();
        return;
      }

      openLeaveConfirm('popstate');
      pushTrap();
    };

    window.addEventListener('popstate', onPopState);
    return () => {
      window.removeEventListener('popstate', onPopState);

      if (leaveConfirmedRef.current || !trapActiveRef.current) return;

      const ownedPath = trapPathRef.current;
      trapActiveRef.current = false;

      const currentPath = window.location.pathname;

      // Moved to another checkout step (Place Order) — do not touch history.
      if (CHECKOUT_PATHS.has(currentPath) && currentPath !== ownedPath) {
        return;
      }
      if (isCheckoutStepTransition()) {
        return;
      }

      // Guard disabled while still on this page (e.g. empty cart): drop trap
      // without history.back() so we never fire popstate on another listener.
      if (
        currentPath === ownedPath &&
        isOwnCheckoutTrap(window.history.state, ownedPath)
      ) {
        window.history.replaceState(null, '');
      }
    };
  }, [guardEnabled, location.pathname, openLeaveConfirm, pushTrap]);

  const title = isProcessing ? 'Order in progress' : 'Leave checkout?';
  const message = isProcessing
    ? 'Your order is still being placed. Leaving now may interrupt checkout. Are you sure you want to go back?'
    : 'Are you sure you want to leave? You may lose progress on this step.';

  const LeaveConfirmModal = showConfirm
    ? createPortal(
        <div
          className="checkout-leave-confirm-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="checkout-leave-title"
          onClick={stayOnCheckout}
        >
          <div className="checkout-leave-confirm-content" onClick={(e) => e.stopPropagation()}>
            <div className="checkout-leave-confirm-header">
              <h3 id="checkout-leave-title">{title}</h3>
              <button
                type="button"
                className="checkout-leave-confirm-close"
                onClick={stayOnCheckout}
                aria-label="Close"
              >
                &times;
              </button>
            </div>
            <div className="checkout-leave-confirm-body">
              <p>{message}</p>
            </div>
            <div className="checkout-leave-confirm-footer">
              <button type="button" className="stay-btn" onClick={stayOnCheckout}>
                Stay on this page
              </button>
              <button type="button" className="leave-btn" onClick={confirmLeave}>
                Leave checkout
              </button>
            </div>
          </div>
        </div>,
        document.body
      )
    : null;

  return { LeaveConfirmModal, openLeaveConfirm };
}
